import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

// Stores y APIs
import useAuthStore from '@store/useAuthStore';
import { getGamesForPlayer, createPlayer, createGame, deletePlayer } from '@/api/gameApi'; // Asegúrate que gameApi exista
import feedback from '@/components/ui/general/feedback';

export function useDashboard({ onPlayerCreated } = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // --- 1. Usamos useAuthStore como única fuente de verdad ---
  const { user, players, activePlayer, setActivePlayer, addPlayer, updatePlayers, removePlayer } = useAuthStore();

  // --- 2. Estados locales para la UI del Dashboard ---
  const [newPlayerName, setNewPlayerName] = useState('');

  // --- 3. Efecto para manejar la selección automática de jugador ---
  useEffect(() => {
    // Si hay jugadores pero ninguno está activo, auto-seleccionar el primero
    if (user && players.length > 0 && !activePlayer) {
      setActivePlayer(players[0].id);
    }
  }, [user, players, activePlayer, setActivePlayer]);

  // --- 4. Queries y Mutations ---
  const { data: unsortedGames = [], error: gameError } = useQuery({
    queryKey: ['games', activePlayer?.id],
    queryFn: () => getGamesForPlayer(activePlayer.id),
    enabled: !!activePlayer,
  });

  // ✅ ORDENAMOS LOS JUEGOS AQUÍ
  // Creamos una copia con [...unsortedGames] para no mutar la caché de React Query
  // y ordenamos por 'id' de forma ascendente (el más bajo primero).
  const games = [...unsortedGames].sort((a, b) => a.id - b.id);

  const createPlayerMutation = useMutation({
    mutationFn: createPlayer,
    onSuccess: (data) => {
      setNewPlayerName('');

      if (data && data.player && data.players) {
        // Update all players with refreshed metadata
        updatePlayers(data.players, data.player);
        feedback.success(`Player "${data.player.name}" created and activated!`);
      }

      queryClient.invalidateQueries({ queryKey: ['user'] }); // Invalida para que fetchUser se vuelva a llamar

      // Close modal if callback provided
      if (onPlayerCreated) {
        onPlayerCreated();
      }
    },
  });

  const createGameMutation = useMutation({
    mutationFn: createGame,
    onSuccess: (newGame) => {
      navigate(`/lobby/${newGame.id}`);
    },
  });

  const deletePlayerMutation = useMutation({
    mutationFn: deletePlayer,
    onSuccess: (data) => {
      // Update all players with refreshed metadata
      if (data && data.players) {
        updatePlayers(data.players, data.activePlayer);
      }

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['games'] });

      // Success feedback is handled by interceptor
    },
    onError: () => {
      // Error logging and feedback handled centrally by api.js
    }
  });

  // --- 5. Handlers ---
  const handleCreatePlayer = () => {
    if (newPlayerName) createPlayerMutation.mutate(newPlayerName);
  };

  const handleSelectPlayer = (e) => {
    const playerId = typeof e === 'object' ? e.target.value : e;
    if (playerId) {
      setActivePlayer(playerId);

      // Show feedback with player name
      const player = players.find(p => p.id === parseInt(playerId));
      if (player) {
        feedback.success(`Player "${player.name}" activated`);
      }
    }
  };

  const handleCreateGame = (gameData) => {
    createGameMutation.mutate(gameData);
  };

  // --- 6. Devolvemos todo lo que la vista necesita ---
  return {
    user,
    players,
    activePlayer,
    games,
    gameError,
    newPlayerName,
    setNewPlayerName,
    handleCreatePlayer,
    handleSelectPlayer,
    handleCreateGame,
    createPlayerMutation,
    createGameMutation,
    deletePlayerMutation,
  };
}